import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Spinner,
  Switch,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderBack, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { useUpdatesSettings } from "#app/lib/hooks/use-updates-settings";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  useReasons,
  useReasonMutations,
  type Reason,
} from "#app/hooks/settings/use-reason-mutations";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { ReasonFormDialog } from "#app/components/settings/reason-form-dialog";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { withPermission } from "#app/components/permissions/require-permission";

function ReasonsPage() {
  const { replace } = useAppNavigate();
  const { settings, hasData, isLoading, error, updateSetting } = useUpdatesSettings();
  const { reasons, isLoading: reasonsLoading } = useReasons();
  const {
    createReason,
    updateReason,
    toggleReason,
    deleteReason,
    setDefaultReason,
    clearDefaultReason,
  } = useReasonMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<Reason | null>(null);
  const [deletingReason, setDeletingReason] = useState<Reason | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  if (isLoading && !hasData) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && !hasData) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load settings</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  const disabled = !settings.reasonsEnabled;
  // Box carries no `opacity`/`pointerEvents` props — the dimmed-and-inert
  // treatment for the reasons-disabled state is an authored class pair.
  const dimmed = disabled ? "opacity-50 pointer-events-none" : undefined;

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Project age updates" onClick={() => replace("/settings/updates")} />
          <PageHeaderTitle>Reasons</PageHeaderTitle>
          <PageHeaderSubtitle>
            Defines why a project's age is updated and adds additional customizable reasons that can be enabled or disabled.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>

          <SettingsGroup label="General">
            <SettingsGroupRow
              label="Enable reasons"
              subtitle="Visible to all workspace members"
            >
              <Switch
                checked={settings.reasonsEnabled}
                onCheckedChange={(v) => updateSetting("reasonsEnabled", v)}
              />
            </SettingsGroupRow>

            <Box className={dimmed}>
              <SettingsGroupRow
                label="Require reason"
                subtitle="A reason must be selected in order to manually update the project age."
              >
                <Switch
                  checked={settings.requireReason}
                  onCheckedChange={(v) => updateSetting("requireReason", v)}
                />
              </SettingsGroupRow>
            </Box>
          </SettingsGroup>

          <Box className={dimmed}>
            <SettingsGroup
              label="Reason choices"
              subtitle="Define reasons why age is being updated."
            >
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {reasons.length} {reasons.length === 1 ? "reason" : "reasons"}
                </Text>
                <Button
                  aria-label="Create reason"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setCreateOpen(true)}
                >
                  <Icon name="plus" size="md" />
                </Button>
              </SettingsGroupHeader>

              {reasonsLoading && reasons.length === 0 ? (
                <Box paddingY="md" align="center" justify="center">
                  <Spinner size="lg" />
                </Box>
              ) : null}

              {reasons.map((reason) => (
                <SettingsGroupRow
                  key={reason.reasonId}
                  label={`${reason.name}${reason.isDefault && !reason.archived ? " · Default" : ""}`}
                  subtitle={reason.archived ? "Disabled" : "Enabled"}
                  leading={
                    <ColorSwatch
                      color={reason.archived ? "grey" : "green"}
                      size="xs"
                      rounded="full"
                    />
                  }
                  actionsVisible={openMenuId === reason.reasonId}
                >
                  <Menu onOpenChange={(open) => setOpenMenuId(open ? reason.reasonId : null)}>
                    <MenuTrigger
                      render={
                        <Button
                          aria-label="Reason actions"
                          variant="ghost"
                          size="icon-xs"
                        />
                      }
                    >
                      <Icon name="more-horizontal" size="md" />
                    </MenuTrigger>
                    <MenuPopup align="end" className="min-w-45">
                      <MenuItem onClick={() => toggleReason(reason)}>
                        <Icon name="eye-off" size="md" />
                        {reason.archived ? "Enable reason" : "Disable reason"}
                      </MenuItem>
                      <MenuItem onClick={() => setEditingReason(reason)}>
                        <Icon name="pen-line" size="md" />
                        Edit
                      </MenuItem>
                      {!reason.archived && !reason.isDefault ? (
                        <MenuItem onClick={() => setDefaultReason(reason.id)}>
                          <Icon name="check" size="md" />
                          Set as default
                        </MenuItem>
                      ) : null}
                      {!reason.archived && reason.isDefault ? (
                        <MenuItem onClick={() => clearDefaultReason()}>
                          <Icon name="x-circle" size="md" />
                          Clear as default
                        </MenuItem>
                      ) : null}
                      <MenuSeparator />
                      <MenuItem
                        variant="destructive"
                        onClick={() => setDeletingReason(reason)}
                      >
                        <Icon name="trash-2" size="md" />
                        Delete...
                      </MenuItem>
                    </MenuPopup>
                  </Menu>
                </SettingsGroupRow>
              ))}
            </SettingsGroup>
          </Box>
          </Box>
        </Box>
      </ScrollArea>

      <ReasonFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={async (name) => {
          await createReason(name);
        }}
      />

      <ReasonFormDialog
        open={!!editingReason}
        onOpenChange={(open) => {
          if (!open) setEditingReason(null);
        }}
        initialName={editingReason?.name}
        onSubmit={async (name) => {
          if (editingReason) {
            await updateReason(editingReason.id, name);
          }
        }}
      />

      <TrashConfirmDialog
        open={!!deletingReason}
        onOpenChange={(open) => {
          if (!open) setDeletingReason(null);
        }}
        title={`Delete "${deletingReason?.name ?? ""}"?`}
        description="This action cannot be undone. Any projects using this reason will no longer have a reason assigned."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deletingReason) {
            await deleteReason(deletingReason);
          }
        }}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/reasons")({
  component: withPermission("reasons.manage", ReasonsPage),
});
